import { Controller } from '@hotwired/stimulus'

/**
 * Auto-refresh controller for polling page updates
 *
 * Automatically refreshes the page at specified intervals
 * Useful for monitoring background job progress
 *
 * @example
 *   <div data-controller="auto-refresh"
 *        data-auto-refresh-interval-value="3000"
 *        data-auto-refresh-url-value="/status.json">
 */
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 3000 },
    url: String
  }

  connect() {
    console.log('Auto-refresh controller connected')
    this.startPolling()
  }

  disconnect() {
    console.log('Auto-refresh controller disconnected')
    this.stopPolling()
  }

  startPolling() {
    if (this.timer) return

    this.timer = setInterval(() => {
      this.checkStatus()
    }, this.intervalValue)
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
      console.log('Polling stopped')
    }
  }

  async checkStatus() {
    try {
      const url = this.hasUrlValue ? this.urlValue : window.location.href
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        console.error('Auto-refresh: HTTP error', response.status)
        this.stopPolling()
        return
      }

      const data = await response.json()
      console.log('Auto-refresh: status =', data.status)

      // Update progress if available
      this.updateProgress(data)

      // Stop polling if job is finished
      if (this.isFinished(data.status)) {
        this.stopPolling()
        // Final refresh to show completed state
        this.refresh()
      }
    } catch (error) {
      console.error('Auto-refresh: fetch error', error)
      this.stopPolling()
    }
  }

  isFinished(status) {
    return ['completed', 'failed', 'cancelled'].includes(status)
  }

  updateProgress(data) {
    // Update progress bar if present
    const progressBar = document.querySelector('[data-progress-bar]')
    if (progressBar && data.progress_percentage !== undefined) {
      progressBar.style.width = `${data.progress_percentage}%`
    }

    // Update progress text if present
    const progressText = document.querySelector('[data-progress-text]')
    if (progressText && data.progress_percentage !== undefined) {
      progressText.textContent = `${data.progress_percentage}%`
    }

    // Update all counts
    const countMappings = {
      'total_count': '[data-total-count]',
      'success_count': '[data-success-count]',
      'error_count': '[data-error-count]',
      'skipped_count': '[data-skipped-count]',
      'pending_count': '[data-pending-count]'
    }

    Object.entries(countMappings).forEach(([key, selector]) => {
      if (data[key] !== undefined) {
        const element = document.querySelector(selector)
        if (element) element.textContent = data[key]
      }
    })
  }

  refresh() {
    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: 'replace' })
    } else {
      window.location.reload()
    }
  }
}
